import { defineStore } from 'pinia';
import { ref } from 'vue';
import { removeToken, setToken, getToken } from '@/utils';
import { resetAllStores } from '@/store';
import { appApi } from '@/plugins/api';
import type { NewLoginPayload as LoginParams } from '@/types/api/login.type';
import type { UserItem } from '@/types/api/account.type';
import type { ChangePasswordQuery } from '@/types/api/systemUser.type';

const useUserStore = defineStore(
  'userInfo',
  () => {
    // 使用全局 appApi，不依赖 Vue 的 inject
    const loginApi = appApi.Login;
    const systemUserApi = appApi.SystemUser;

    const token = ref<string>('');
    const userInfo = ref<UserItem | null>(null);
    const passwordExpired = ref<boolean>(false);

    //设置密码过期
    const setPasswordExpired = (value: boolean) => {
      passwordExpired.value = value;
    };

    //获取密码过期
    const getPasswordExpired = () => {
      return passwordExpired.value;
    };

    // 登录
    const loginAction = async (params: LoginParams) => {
      try {
        const res = await loginApi.login(params);

        // res.data 是 ApiResponse<NewLoginedData>，res.data.data 是 NewLoginedData
        token.value = res.data.data.token;
        setToken(res.data.data.token);

        return Promise.resolve(res);
      } catch (error) {
        return Promise.reject(error);
      }
    };
    // 修改密码
    const changePasswordAction = async (params: ChangePasswordQuery) => {
      try {
        const res = await systemUserApi.changePassword(params);

        return Promise.resolve(res);
      } catch (error) {
        return Promise.reject(error);
      }
    };

    // 获取用户信息
    const getUserInfoAction = async () => {
      try {
        const res = await systemUserApi.getCurrentUserInfo();

        // res.data 是 ApiResponse<UserItem>，res.data.data 是 UserItem
        userInfo.value = res.data.data;

        return Promise.resolve(res.data.data);
      } catch (error) {
        return Promise.reject(error);
      }
    };

    // 登出
    const logoutAction = async () => {
      try {
        // 1. 调用登出 API
        await loginApi.logout({ uuid: userInfo.value?.uuid || '' });

        // 2. 重置所有 store（自动清除所有持久化数据）
        resetAllStores();

        // 3. 跳转到登录页
        window.location.href = '/login';

        return Promise.resolve();
      } catch (error) {
        return Promise.reject(error);
      }
    };

    // 重置状态
    const resetState = () => {
      token.value = '';
      userInfo.value = null;
      passwordExpired.value = false;
      removeToken();
    };

    //获取token
    const getTokenAction = () => {
      return token.value || getToken() || '';
    };
    //是否为管理员
    const isAdmin = computed(() => {
      return userInfo.value?.userName === 'admin';
    });

    return {
      token,
      getTokenAction,
      userInfo,
      passwordExpired,
      isAdmin,
      login: loginAction,
      getUserInfo: getUserInfoAction,
      logout: logoutAction,
      resetState,
      setPasswordExpired,
      getPasswordExpired,
      changePassword: changePasswordAction

    };
  },
  {
    persist: {
      key: 'userInfo-store',
      storage: localStorage,
      paths: ['token', 'passwordExpired', 'userInfo']
    }
  }
);

export default useUserStore;